import { Box } from "@mui/material";
import {
  GridColDef,
  GridPaginationModel,
  GridSortItem,
} from "@mui/x-data-grid";
import { useState } from "react";
import { useOutletContext } from "react-router-dom";
import ActionMenu from "@/components/app/ActionMenu";
import UpsertReferringAgencyDrawer from "@/components/app/admin/provider-groups/UpsertReferringAgencyDrawer";
import DeleteModal from "@/components/app/DeleteModal";
import CustomDataGridV2 from "@/components/CustomDataGridV2";
import Text from "@/components/Text";
import ToggleSwitch from "@/components/ToggleSwitch";
import {
  REFERRING_AGENCY_ACTION as ACTION,
  REFERRING_AGENCY_MESSAGES,
} from "@/constants/referring-agency-constant";
import useDisclosure from "@/hooks/use-disclosure";
import { useSnackbar } from "@/hooks/useSnackbar";
import {
  useArchiveReferringAgency,
  useReferringAgencyList,
  useUpdateReferringAgency,
} from "@/queries/use-referring-agency";
import { ReferringAgency } from "@/services";
import { Identified } from "@/types/sdk-types";
import {
  formatReferringAgencyAddress,
  toReferringAgencyFormValues,
  toReferringAgencyPayload,
} from "@/utils/referring-agency-utils";
import { DEFAULT_PAGE_SIZE, getErrorSnackbar } from "@/utils/utils";

type ReferringAgencyRow = Identified<ReferringAgency>;

// The theme paints a disabled thumb near-white on the white track; keep the standard thumb, just faded.
const ARCHIVED_SWITCH_SX = {
  "& .MuiSwitch-switchBase.Mui-disabled": { opacity: 0.5 },
  "& .MuiSwitch-switchBase.Mui-disabled .MuiSwitch-thumb": { color: "#08345A" },
};

const ProviderGroupReferringAgency = () => {
  const showSnackbar = useSnackbar();
  const {
    searchKey,
    showArchivedReferringAgency: isArchivedView,
  }: {
    searchKey: string;
    showArchivedReferringAgency: boolean;
  } = useOutletContext();
  const [pagination, setPagination] = useState<GridPaginationModel>({
    page: 0,
    pageSize: DEFAULT_PAGE_SIZE,
  });
  const [gridSorting, setGridSorting] = useState<GridSortItem[]>([]);

  const agencyDrawer = useDisclosure(false);
  const statusConfirmation = useDisclosure(false);

  const { data, isLoading, isFetching, isError } = useReferringAgencyList({
    pagination,
    gridSorting,
    searchKey,
    isArchived: isArchivedView,
  });
  const { mutateAsync: archiveAgency } = useArchiveReferringAgency();
  const { mutateAsync: updateAgency } = useUpdateReferringAgency();

  const rows = data?.data?.content ?? [];

  async function changeStatus() {
    const agency: ReferringAgencyRow = statusConfirmation.data;
    const isRestoring = !!agency.archive;

    try {
      await archiveAgency({
        referringAgencyId: agency.uuid,
        isArchived: !isRestoring,
      });
      showSnackbar({
        type: "success",
        message: isRestoring
          ? REFERRING_AGENCY_MESSAGES.RESTORED
          : REFERRING_AGENCY_MESSAGES.ARCHIVED,
      });
    } catch (error) {
      showSnackbar(getErrorSnackbar(error));
    } finally {
      statusConfirmation.close();
    }
  }

  const changeActiveStatus = (agency: ReferringAgencyRow, active: boolean) =>
    updateAgency({
      ...toReferringAgencyPayload(toReferringAgencyFormValues(agency)),
      active,
    });

  function onActionClick(action: string, agency: ReferringAgencyRow) {
    if (action === ACTION.EDIT)
      agencyDrawer.open(toReferringAgencyFormValues(agency));
    else statusConfirmation.open(agency);
  }

  const columns: GridColDef<ReferringAgencyRow>[] = [
    { field: "agencyId", headerName: "Agency ID", flex: 0.7, minWidth: 120 },
    {
      field: "name",
      headerName: "Referring Agency Name",
      flex: 1.2,
      minWidth: 200,
    },
    { field: "agencyType", headerName: "Specialties", flex: 1, minWidth: 180 },
    {
      field: "address",
      headerName: "Address",
      flex: 1.2,
      minWidth: 200,
      sortable: false,
      renderCell: ({ row }) => <Text>{formatReferringAgencyAddress(row)}</Text>,
    },
    { field: "phone", headerName: "Contact Number", flex: 1, minWidth: 150 },
    {
      field: "active",
      headerName: "Status",
      flex: 0.7,
      minWidth: 110,
      sortable: false,
      renderCell: ({ row }) => (
        <ToggleSwitch
          id={row.uuid}
          checked={!!row.active}
          readOnly={!!row.archive}
          sx={ARCHIVED_SWITCH_SX}
          content={
            row.active ? REFERRING_AGENCY_MESSAGES.INACTIVATE : undefined
          }
          onConfirm={(active) => changeActiveStatus(row, active)}
        />
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      sortable: false,
      renderCell: ({ row }) => (
        <ActionMenu
          options={
            isArchivedView
              ? [{ value: ACTION.RESTORE, label: "Restore" }]
              : [
                  { value: ACTION.EDIT, label: "Edit" },
                  {
                    value: ACTION.ARCHIVE,
                    label: "Archive",
                    isDisabled: !!row.active,
                    hoverMsg: REFERRING_AGENCY_MESSAGES.ARCHIVE_DISABLED,
                  },
                ]
          }
          onActionClick={(action) => onActionClick(action, row)}
        />
      ),
    },
  ];

  return (
    <Box width="100%">
      {isError && (
        <Text c="error.main" mb={1}>
          {REFERRING_AGENCY_MESSAGES.LOAD_FAILED}
        </Text>
      )}
      <Box height="calc(100vh - 22rem)">
        <CustomDataGridV2
          loading={isLoading || isFetching}
          searchString={searchKey}
          getRowId={(row) => row.uuid}
          rows={rows}
          columns={columns}
          rowCount={data?.data?.page?.totalElements ?? 0}
          paginationMode="server"
          sortingMode="server"
          sortModel={gridSorting}
          onSortModelChange={setGridSorting}
          disableColumnMenu
          paginationModel={pagination}
          onPaginationModelChange={setPagination}
        />
      </Box>

      {agencyDrawer.opened && (
        <UpsertReferringAgencyDrawer
          open={agencyDrawer.opened}
          preFillValues={agencyDrawer.data}
          onClose={agencyDrawer.close}
        />
      )}

      <DeleteModal
        w="380px"
        variant="warning"
        open={statusConfirmation.opened}
        onClose={statusConfirmation.close}
        content={
          isArchivedView
            ? REFERRING_AGENCY_MESSAGES.RESTORE
            : REFERRING_AGENCY_MESSAGES.ARCHIVE
        }
        onConfirmation={changeStatus}
        disableOnSaveSnackbar
      />
    </Box>
  );
};

export default ProviderGroupReferringAgency;
